(ns machtfit.app.tracking
  (:require [clojure.string :as str]
            [goog.Uri]
            [machtfit.utils :as utils]
            [re-frame.core :as rf]
            [reitit.frontend]))

(defn data-from-match [match]
  (let [tracking-fn (get-in match [:data :tracking :page-data-fn])]
    (cond tracking-fn (tracking-fn match)
          (not match) {:section :error
                       :type :404}
          :else nil)))

(defn data-from-url [router url]
  (let [match (reitit.frontend/match-by-path router url)]
    (data-from-match match)))

(defn- capture-with-posthog!
  ([event-name properties]
   (when (exists? js/posthog)
     (.capture js/posthog event-name (clj->js properties))))
  ([event-name properties options]
   (when (exists? js/posthog)
     (.capture js/posthog event-name (clj->js properties) (clj->js options)))))

(defn register-super-properties [properties]
  (when (exists? js/posthog)
    (.register js/posthog (clj->js properties))))

(rf/reg-fx ::register-super-properties
  (fn [properties]
    (register-super-properties properties)))

(defn unregister-super-properties [property-keys]
  (when (exists? js/posthog)
    (doseq [key property-keys]
      (.unregister js/posthog (name key)))))

(rf/reg-fx ::unregister-super-properties
  (fn [property-keys]
    (unregister-super-properties property-keys)))

(rf/reg-fx ::capture-pageview
  (fn [properties]
    (capture-with-posthog! "$pageview" (assoc-in properties [:page :is_spa] true))))

(defn- sleep-synchronously [ms]
  (let [continue-at (+ (js/Date.now) ms)]
    (while (< (js/Date.now) continue-at)
      nil)))

(rf/reg-fx ::capture-pageleave
  (fn [properties]
    ;; we sleep here to avoid that PageLeave/Pageview and their
    ;; triggering event occur on the same Millisecond,
    ;; thus messing up their order in PostHog
    (sleep-synchronously 2)
    (capture-with-posthog! "$pageleave" properties)
    (sleep-synchronously 2)))

(rf/reg-cofx ::url-properties
  (fn [cofx _]
    (let [url js/window.location.href
          uri (new goog.Uri url)]
      (assoc cofx :url-properties
             {:url url
              :url_path (.getPath uri)
              :url_query_string (.getQuery uri)}))))

(rf/reg-event-fx ::pageview
  [(rf/inject-cofx ::url-properties)]
  (fn [{:keys [url-properties]} [_ properties super-properties-to-be-merged]]
    (let [properties* (update properties :page merge
                              {:is_spa true}
                              url-properties)]
      {:fx (remove nil?
                   [[::register-super-properties properties*]
                    (when super-properties-to-be-merged [::deep-merge-super-properties super-properties-to-be-merged])
                    [::capture-pageview properties*]])})))

(defn coerce-key [s] (-> s
                         str/lower-case
                         (str/replace #"[ -]" "_")
                         (str/replace #"[^a-z0-9_]" "")))

(defn coerce-keys [properties-map]
  (reduce-kv
   (fn [m k v]
     (assoc m
            (coerce-key (name k))
            (if (map? v)
              (coerce-keys v)
              v)))
   {}
   properties-map))

(defn- deep-merge-super-properties [properties]
  (when (exists? js/posthog)
    (when-let [current-super-properties (some-> js/posthog
                                                .-persistence
                                                .-props
                                                (js->clj :keywordize-keys true))]
      (register-super-properties (utils/deep-merge current-super-properties properties)))))

(defn- assoc-in-super-properties [path value]
  (when (exists? js/posthog)
    (let [current-super-properties (some-> js/posthog
                                           .-persistence
                                           .-props
                                           (js->clj :keywordize-keys true))]
      (register-super-properties (assoc-in current-super-properties path value)))))

(rf/reg-fx ::deep-merge-super-properties
  (fn [properties]
    (deep-merge-super-properties properties)))

(rf/reg-fx ::assoc-in-super-properties
  (fn [[path value]]
    (assoc-in-super-properties path value)))

;; changes to this effect must be coordinated with the Data team
(defn- capture-event! [db event-name ph-ui-id event-type properties & [options]]
  (let [event-properties (cond-> {"event" {"name" event-name}
                                  "language" (:language db)}
                           ph-ui-id (assoc "ui_id" ph-ui-id)
                           event-type (assoc "$event_type" event-type)
                           properties (utils/deep-merge {"event" (coerce-keys properties)}))]
    (if options
      (capture-with-posthog! (str "custom_" (name event-name)) event-properties options)
      (capture-with-posthog! (str "custom_" (name event-name)) event-properties))))

(defn id [feature element] (str feature "__" element))

(defn compose-ui-id [& segments]
  (->> segments
       (remove nil?)
       (map (comp coerce-key name))
       (reduce id)))

(rf/reg-fx ::capture-event
  (fn [[db event-name properties]]
    (capture-event! db event-name nil nil properties)))

(def before-navigation {:send_instantly true :transport "sendBeacon"})

(rf/reg-fx ::capture-ui-event
  (fn [[db event-name ph-ui-id properties options]]
    (capture-event! db event-name ph-ui-id nil properties options)))

(rf/reg-fx ::capture-click
  (fn [[db event-name ph-ui-id properties]]
    (capture-event! db event-name ph-ui-id "click" properties)))

(rf/reg-event-fx ::click
  (fn [{:keys [db]} [_ event-name ph-ui-id properties]]
    {::capture-click [db event-name ph-ui-id properties]}))
